import { useState, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { type SxProps, type Theme } from "@mui/material/styles";

import { Box } from "ui/box";
import { Dialog, DialogActions } from "ui/dialog";
import { Button } from "ui/button";
import { Typography } from "ui/typography";
import { CloneTouchpointModal } from "components/modals/clone-touchpoint-modal/clone-touchpoint-modal";
import TouchpointProvider from "containers/admin/clients/touchpoint/components/touchpoint-provider/touchpoint-provider";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useTouchpoint } from "hooks/api/use-touchpoints";
import Icons from "assets";

import type { TouchpointAttributes } from "hooks/api/use-touchpoints";
import { folderKeys, touchpointKeys } from "hooks/api/query-keys";
import { getActionTouchpointTypeLabel } from "utilities";

/**
 * Holds the card near the loaded `CloneTouchpointModal`'s height (≈648px: a 552px body plus 96px of
 * padding, headline and gap), so the form replaces it without a jump. `medium` caps the card at 80vh,
 * so the body is capped by the same amount.
 */
const LOADING_BODY_SX: SxProps<Theme> = {
  height: "min(552px, calc(80vh - 96px))",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const LOADING_STATUS_SX: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 1,
  color: theme.palette.text.secondary,
});

export const useCloneTouchpointModal = (onSuccess?: () => void) => {
  const [cloningTouchpoint, setCloningTouchpoint] = useState<TouchpointAttributes | null>(null);
  const currentCampaign = useCurrentCampaign();
  const queryClient = useQueryClient();
  const { data: providerTouchpoint, isError: isTouchpointError } = useTouchpoint(cloningTouchpoint?.id ?? "", {
    enabled: !!cloningTouchpoint?.id,
  });

  const openCloneTouchpointModal = useCallback((touchpoint: TouchpointAttributes) => {
    setCloningTouchpoint(touchpoint);
  }, []);

  const closeCloneTouchpointModal = useCallback(() => {
    setCloningTouchpoint(null);
  }, []);

  const handleCloneSuccess = useCallback(() => {
    // `useCloneTouchpointVersion` invalidates its own version trees, but the
    // creative table is driven by touchpoint + folder lists, so we still need
    // to invalidate those here.
    queryClient.invalidateQueries({ queryKey: touchpointKeys.lists() });
    queryClient.invalidateQueries({ queryKey: folderKeys.lists() });
    onSuccess?.();
    closeCloneTouchpointModal();
  }, [queryClient, closeCloneTouchpointModal, onSuccess]);

  const cloneTouchpointModalMarkup = useMemo(() => {
    if (!cloningTouchpoint) return null;

    const latestVersionId = cloningTouchpoint.latestVersion?.id;
    const latestVersionVersion = cloningTouchpoint.latestVersion?.version;
    const cloningTouchpointTypeLabel = getActionTouchpointTypeLabel(cloningTouchpoint.type).toLowerCase();

    const noticeDialog = (description: string) => (
      <Dialog
        open
        onClose={closeCloneTouchpointModal}
        size="small"
        headline={`Clone ${cloningTouchpointTypeLabel}`}
        description={description}
        actions={
          <DialogActions
            primary={
              <Button size="large" onClick={closeCloneTouchpointModal}>
                Close
              </Button>
            }
          />
        }
      />
    );

    // The user asked to open the modal, so we must never "disappear".
    // If there is nothing to clone - keep the modal open and explain why.
    if (!latestVersionId) {
      return noticeDialog(`This ${cloningTouchpointTypeLabel} doesn't have a version to clone yet.`);
    }

    // A failed load would otherwise leave the loading dialog spinning for good: the form below waits for data
    // that is not coming.
    if (isTouchpointError) {
      return noticeDialog(
        `Could not load this ${cloningTouchpointTypeLabel}. Please close the dialog and try again.`,
      );
    }

    const isProviderReady = providerTouchpoint?.id === cloningTouchpoint.id;

    /* Rendered in place of the form, never beside it. `TouchpointProvider` would hold the form back on its own
       until the touchpoint loads, but only because its query shares this hook's key; gating here keeps one
       dialog open — one focus trap, one history entry — if the two ever diverge. */
    if (!isProviderReady) {
      return (
        <Dialog
          open
          onClose={closeCloneTouchpointModal}
          size="medium"
          headline={`Clone ${cloningTouchpointTypeLabel}`}>
          <Box sx={LOADING_BODY_SX}>
            <Box sx={LOADING_STATUS_SX}>
              <Icons.Loader customClass="animate-spin size-5" />
              <Typography component="span" variant="body1">
                Loading {cloningTouchpointTypeLabel} details…
              </Typography>
            </Box>
          </Box>
        </Dialog>
      );
    }

    return (
      <TouchpointProvider
        currentTouchpointId={cloningTouchpoint.id}
        currentCampaignId={currentCampaign.id}>
        <CloneTouchpointModal
          isOpen={true}
          onClose={closeCloneTouchpointModal}
          onSuccess={handleCloneSuccess}
          selectedVersion={{
            id: latestVersionId,
            touchpointName: cloningTouchpoint.name,
            campaignName: currentCampaign.name || "",
            version: latestVersionVersion,
            campaignId: currentCampaign.id,
            touchpointId: cloningTouchpoint.id,
            name: cloningTouchpoint.name,
            fiscalYear: cloningTouchpoint.latestVersion?.fiscalYear
          }}
        />
      </TouchpointProvider>
    );
  }, [
    cloningTouchpoint,
    providerTouchpoint?.id,
    isTouchpointError,
    currentCampaign.id,
    currentCampaign.name,
    closeCloneTouchpointModal,
    handleCloneSuccess,
  ]);

  return {
    openCloneTouchpointModal,
    closeCloneTouchpointModal,
    cloneTouchpointModalMarkup,
  };
};
